import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import {
  discardWebsiteBuilderDraft,
  fetchWebsiteBuilderConfig,
  publishWebsiteBuilderConfig,
  saveWebsiteBuilderDraft,
  saveWebsiteThemeBaseline,
} from "modules/website-builder/services/api"
import {
  normalizeWebsiteBuilderForm,
  cloneWebsiteConfig,
  extractWebsiteSnapshot,
  applyWebsiteSnapshotToForm,
  normalizeWebsiteBuilderType,
  getWebsiteBuilderTypeLabelKey,
  getWebsiteBuilderTypeDescriptionKey,
} from "modules/website-builder/constants"
import {
  AnimationsTab,
  BrandThemeTab,
  FeaturesTab,
  FooterContactTab,
  HomeTab,
  PreviewPublishTab,
  TemplatesTab,
} from "modules/website-builder/pages/WebsiteBuilderFields"
import { WebsiteBuilderTenantContext } from "modules/website-builder/WebsiteBuilderTenantContext"

const TABS = [
  { id: "templates", label: "Templates" },
  { id: "brand", label: "Brand & Theme" },
  { id: "home", label: "Home Page" },
  { id: "footer", label: "Footer & Contact" },
  { id: "features", label: "Features" },
  { id: "animations", label: "Animations" },
  { id: "publish", label: "Preview & Publish" },
]

const WebsiteBuilder = ({
  tenantId,
  tenantLabel = "",
  breadcrumbRoot = "Website Builder",
  readOnly = false,
  websiteType: websiteTypeProp,
}) => {
  const websiteType = normalizeWebsiteBuilderType(websiteTypeProp)
  const builderTitleKey = getWebsiteBuilderTypeLabelKey(websiteType)
  const builderDescriptionKey = getWebsiteBuilderTypeDescriptionKey(websiteType)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState("templates")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [form, setForm] = useState(null)
  const [publishedSnapshot, setPublishedSnapshot] = useState(null)
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false)
  const [publishedAt, setPublishedAt] = useState(null)
  const [tenantWebsiteUrl, setTenantWebsiteUrl] = useState("")
  const [allowedFeatures, setAllowedFeatures] = useState(null)
  const [webThemes, setWebThemes] = useState([])
  const [layoutTemplates, setLayoutTemplates] = useState([])
  const [savedThemeBaseline, setSavedThemeBaseline] = useState(null)
  const [savedThemeBaselineAt, setSavedThemeBaselineAt] = useState(null)
  const [savingThemeBaseline, setSavingThemeBaseline] = useState(false)
  const [restoringThemeBaseline, setRestoringThemeBaseline] = useState(false)
  const formRef = useRef(null)

  document.title = tenantLabel
    ? `${t(builderTitleKey)} — ${tenantLabel}`
    : t(builderTitleKey)

  const applyFormChange = useCallback(updater => {
    const base = formRef.current
    if (!base) return

    const next = typeof updater === "function" ? updater(base) : updater
    if (!next) return

    const cloned = cloneWebsiteConfig(next)
    formRef.current = cloned
    setForm(cloned)
    setHasUnpublishedChanges(true)
  }, [])

  const loadConfig = useCallback(async () => {
    if (!tenantId) return

    setLoading(true)
    try {
      const response = await fetchWebsiteBuilderConfig(tenantId, websiteType)
      if (response?.status && response?.data) {
        const draft = normalizeWebsiteBuilderForm(response.data.draft || {})
        formRef.current = draft
        setForm(draft)
        setPublishedSnapshot(
          response.data.published
            ? normalizeWebsiteBuilderForm(response.data.published)
            : null
        )
        setHasUnpublishedChanges(!!response.data.hasUnpublishedChanges)
        setPublishedAt(response.data.publishedAt || null)
        setTenantWebsiteUrl(response.data.tenantWebsiteUrl || "")
        setAllowedFeatures(
          Array.isArray(response.data.allowedFeatures) ? response.data.allowedFeatures : null
        )
        setWebThemes(Array.isArray(response.data.webThemes) ? response.data.webThemes : [])
        setLayoutTemplates(Array.isArray(response.data.templates) ? response.data.templates : [])
        setSavedThemeBaseline(response.data.savedThemeBaseline || null)
        setSavedThemeBaselineAt(response.data.savedThemeBaselineAt || null)
      }
    } catch (_err) {
      dispatch(
        showAlert({
          text: t("Unable to load website builder settings"),
          type: "error",
        })
      )
    } finally {
      setLoading(false)
    }
  }, [dispatch, t, tenantId, websiteType])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  const saveDraft = async ({ showSuccessAlert = true } = {}) => {
    if (readOnly) return false
    const currentForm = formRef.current
    if (!currentForm || !tenantId) return false
    setSaving(true)
    try {
      const response = await saveWebsiteBuilderDraft(tenantId, currentForm, websiteType)
      if (response?.status) {
        if (showSuccessAlert) {
          dispatch(showAlert({ text: t("Draft saved successfully"), type: "success" }))
        }
        if (response?.data?.draft) {
          const savedDraft = normalizeWebsiteBuilderForm(response.data.draft)
          formRef.current = savedDraft
          setForm(savedDraft)
        }
        return true
      }

      dispatch(
        showAlert({
          text: response?.msg || t("Unable to save website draft"),
          type: "error",
        })
      )
      return false
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to save website draft"), type: "error" }))
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleSaveDraft = () => saveDraft({ showSuccessAlert: true })

  const handlePublish = async () => {
    if (readOnly) return
    const currentForm = formRef.current
    if (!currentForm || !tenantId) return

    setPublishing(true)
    try {
      const saved = await saveDraft({ showSuccessAlert: false })
      if (!saved) return

      const response = await publishWebsiteBuilderConfig(tenantId, websiteType)
      if (response?.status) {
        dispatch(showAlert({ text: t("Website published successfully"), type: "success" }))
        await loadConfig()
      } else {
        dispatch(
          showAlert({
            text: response?.msg || t("Unable to publish website"),
            type: "error",
          })
        )
      }
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to publish website"), type: "error" }))
    } finally {
      setPublishing(false)
    }
  }

  const handleDiscard = async () => {
    if (readOnly || !tenantId) return

    try {
      const response = await discardWebsiteBuilderDraft(tenantId, websiteType)
      if (response?.status) {
        dispatch(showAlert({ text: t("Draft changes discarded"), type: "success" }))
        await loadConfig()
      }
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to discard draft"), type: "error" }))
    }
  }

  const handleSaveThemeBaseline = async () => {
    if (readOnly) return
    const currentForm = formRef.current
    if (!currentForm || !tenantId) return

    const snapshot = extractWebsiteSnapshot(currentForm)
    setSavingThemeBaseline(true)
    try {
      const response = await saveWebsiteThemeBaseline(tenantId, snapshot, websiteType)
      if (response?.status) {
        setSavedThemeBaseline(response.data?.savedThemeBaseline || snapshot)
        setSavedThemeBaselineAt(response.data?.savedThemeBaselineAt || new Date().toISOString())
        dispatch(
          showAlert({
            text: t("Your website design has been saved. Use Reset to restore it anytime."),
            type: "success",
          })
        )
      } else {
        dispatch(
          showAlert({
            text: response?.msg || t("Unable to save website snapshot"),
            type: "error",
          })
        )
      }
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to save website snapshot"), type: "error" }))
    } finally {
      setSavingThemeBaseline(false)
    }
  }

  const handleRestoreThemeBaseline = async () => {
    if (readOnly || !tenantId) return

    setRestoringThemeBaseline(true)
    try {
      const response = await fetchWebsiteBuilderConfig(tenantId, websiteType)
      const snapshot = response?.data?.savedThemeBaseline

      if (!response?.status || !snapshot?.theme) {
        dispatch(
          showAlert({
            text: t(
              "No saved design found. Customize your website and click Save My Design first."
            ),
            type: "error",
          })
        )
        return
      }

      setSavedThemeBaseline(snapshot)
      setSavedThemeBaselineAt(response.data?.savedThemeBaselineAt || null)
      applyFormChange(prev => applyWebsiteSnapshotToForm(prev, snapshot))
      dispatch(
        showAlert({
          text: t("Design restored from your saved version. Save draft to keep changes."),
          type: "success",
        })
      )
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to restore saved design"), type: "error" }))
    } finally {
      setRestoringThemeBaseline(false)
    }
  }

  if (!tenantId) {
    return null
  }

  if (loading || !form) {
    return (
      <div className="page-content">
        <Breadcrumbs title={t(breadcrumbRoot)} breadcrumbItem={t(builderTitleKey)} />
        <Card>
          <CardBody>{t("Loading...")}</CardBody>
        </Card>
      </div>
    )
  }

  return (
    <WebsiteBuilderTenantContext.Provider value={{ tenantId, websiteType }}>
      <div className="page-content">
        <Breadcrumbs title={t(breadcrumbRoot)} breadcrumbItem={t(builderTitleKey)} />

        <Card>
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div>
                <h4 className="mb-1">{t(builderTitleKey)}</h4>
                {tenantLabel ? (
                  <p className="text-muted mb-1">
                    {t("Tenant")}: <strong>{tenantLabel}</strong>
                  </p>
                ) : null}
                <p className="text-muted mb-0">{t(builderDescriptionKey)}</p>
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {hasUnpublishedChanges ? (
                  <Badge color="warning">{t("Unpublished changes")}</Badge>
                ) : (
                  <Badge color="success">{t("Published")}</Badge>
                )}
                <Button color="light" onClick={handleDiscard} disabled={readOnly || !hasUnpublishedChanges}>
                  {t("Discard Draft")}
                </Button>
                <Button color="primary" outline onClick={handleSaveDraft} disabled={readOnly || saving}>
                  {saving ? t("Saving...") : t("Save Draft")}
                </Button>
                <Button color="primary" onClick={handlePublish} disabled={readOnly || publishing}>
                  {publishing ? t("Publishing...") : t("Publish")}
                </Button>
              </div>
            </div>

            <Nav tabs className="nav-tabs-custom mb-3">
              {TABS.map(tab => (
                <NavItem key={tab.id}>
                  <NavLink
                    className={activeTab === tab.id ? "active" : ""}
                    onClick={() => setActiveTab(tab.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {t(tab.label)}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="templates">
                {/* Color themes + layout templates */}
                <TemplatesTab
                  form={form}
                  webThemes={webThemes}
                  templates={layoutTemplates}
                  publishedWebThemeSlug={publishedSnapshot?.webThemeSlug}
                  publishedTemplateId={publishedSnapshot?.templateId}
                  t={t}
                  onChange={applyFormChange}
                />
              </TabPane>
              <TabPane tabId="brand">
                <BrandThemeTab
                  form={form}
                  t={t}
                  onChange={applyFormChange}
                  savedThemeBaseline={savedThemeBaseline}
                  savedThemeBaselineAt={savedThemeBaselineAt}
                  savingThemeBaseline={savingThemeBaseline}
                  restoringThemeBaseline={restoringThemeBaseline}
                  onSaveThemeBaseline={handleSaveThemeBaseline}
                  onRestoreThemeBaseline={handleRestoreThemeBaseline}
                />
              </TabPane>
              <TabPane tabId="home">
                <HomeTab form={form} t={t} onChange={applyFormChange} />
              </TabPane>
              <TabPane tabId="footer">
                <FooterContactTab form={form} t={t} onChange={applyFormChange} />
              </TabPane>
              <TabPane tabId="features">
                <FeaturesTab
                  form={form}
                  t={t}
                  onChange={applyFormChange}
                  allowedFeatures={allowedFeatures}
                />
              </TabPane>
              <TabPane tabId="animations">
                <AnimationsTab form={form} t={t} onChange={applyFormChange} />
              </TabPane>
              <TabPane tabId="publish">
                <PreviewPublishTab
                  form={form}
                  t={t}
                  onChange={applyFormChange}
                  tenantWebsiteUrl={tenantWebsiteUrl}
                  publishedAt={publishedAt}
                  publishedSnapshot={publishedSnapshot}
                  hasUnpublishedChanges={hasUnpublishedChanges}
                  webThemes={webThemes}
                />
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </div>
    </WebsiteBuilderTenantContext.Provider>
  )
}

export default WebsiteBuilder
